import { DateRangePicker, type DateRange } from "@nexus/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  CUSTOM_PRESET_ID,
  buildExplorePresets,
  isCatalogPresetId,
  resolvePresetWindow,
  resolveRollingWindow,
} from "@/features/dashboard/lib/explore-date-presets";
import { formatYmd, parseYmdLocal } from "@/features/dashboard/lib/ymd";

export const LAST_60_DAYS_PRESET_ID = "last_60_days";

export interface CreateSetDateSelection {
  startDate: string;
  endDate: string;
  activePresetId: string;
}

export interface ExtraRollingPreset {
  id: string;
  days: number;
  defaultLabel: string;
}

export const PERSONA_EXTRA_ROLLING_PRESET: ExtraRollingPreset = {
  id: LAST_60_DAYS_PRESET_ID,
  days: 60,
  defaultLabel: "Last 60 days",
};

export interface CreateSetDateRangePickerProps {
  startDate: string;
  endDate: string;
  activePresetId: string;
  onChange: (next: CreateSetDateSelection) => void;
  extraRollingPresets?: ExtraRollingPreset[];
  today?: Date;
  disabled?: boolean;
  id?: string;
}

function resolveCreateSetPresetWindow(
  presetId: string,
  today: Date,
  extraRollingPresets: ExtraRollingPreset[],
) {
  const extra = extraRollingPresets.find((preset) => preset.id === presetId);
  if (extra) return resolveRollingWindow(extra.days, today);
  return resolvePresetWindow(presetId, today);
}

function isResolvablePresetId(presetId: string, extraRollingPresets: ExtraRollingPreset[]): boolean {
  return isCatalogPresetId(presetId) || extraRollingPresets.some((preset) => preset.id === presetId);
}

/**
 * Explore date picker for create-set modals. Persona injects `last_60_days`
 * here — it must not land on the shared Explore rail.
 */
export function CreateSetDateRangePicker({
  startDate,
  endDate,
  activePresetId,
  onChange,
  extraRollingPresets = [],
  today,
  disabled,
  id,
}: CreateSetDateRangePickerProps) {
  const { t } = useTranslation();
  const referenceDate = useMemo(() => today ?? new Date(), [today]);

  const presets = useMemo(() => {
    const base = buildExplorePresets(t, referenceDate);
    if (extraRollingPresets.length === 0) return base;

    const extras = extraRollingPresets.map((preset) => {
      const window = resolveRollingWindow(preset.days, referenceDate);
      return {
        id: preset.id,
        label: t(`dashboard.dateRangePicker.presets.${preset.id}`, preset.defaultLabel),
        range: {
          from: parseYmdLocal(window.start) as Date,
          to: parseYmdLocal(window.end) as Date,
        },
      };
    });

    const insertAfter = base.findIndex((preset) => preset.id === "last_30_days");
    if (insertAfter < 0) return [...base, ...extras];
    return [...base.slice(0, insertAfter + 1), ...extras, ...base.slice(insertAfter + 1)];
  }, [t, referenceDate, extraRollingPresets]);

  const value = useMemo<DateRange | undefined>(() => {
    const from = parseYmdLocal(startDate ?? "");
    if (from) {
      const to = parseYmdLocal(endDate);
      return { from, to: to ?? undefined };
    }
    const resolved = resolveCreateSetPresetWindow(activePresetId, referenceDate, extraRollingPresets);
    if (!resolved) return undefined;
    return {
      from: parseYmdLocal(resolved.start) ?? undefined,
      to: parseYmdLocal(resolved.end) ?? undefined,
    };
  }, [startDate, endDate, activePresetId, referenceDate, extraRollingPresets]);

  function handleCommit(next: { range: DateRange | undefined; presetId: string | undefined }) {
    if (next.presetId && isResolvablePresetId(next.presetId, extraRollingPresets)) {
      const window = resolveCreateSetPresetWindow(next.presetId, referenceDate, extraRollingPresets);
      if (!window) return;
      onChange({
        startDate: window.start,
        endDate: window.end,
        activePresetId: next.presetId,
      });
      return;
    }

    if (!next.range?.from || !next.range.to) return;
    onChange({
      startDate: formatYmd(next.range.from),
      endDate: formatYmd(next.range.to),
      activePresetId: CUSTOM_PRESET_ID,
    });
  }

  return (
    <DateRangePicker
      id={id}
      value={value}
      onChange={() => undefined}
      onCommit={handleCommit}
      presets={presets}
      activePresetId={activePresetId}
      disabled={disabled}
      toDate={referenceDate}
      labels={{
        placeholder: t("dashboard.dateRangePicker.placeholder", "Select dates"),
        triggerAria: t("dashboard.dateRangePicker.triggerAria", "Select date range"),
        presetList: t("dashboard.dateRangePicker.presetList", "Date range presets"),
        clear: t("dashboard.dateRangePicker.clear", "Clear"),
        apply: t("dashboard.dateRangePicker.apply", "Apply"),
        custom: t("dashboard.dateRangePicker.custom", "Custom"),
      }}
    />
  );
}
